/**
 * Sync Manager - Handles background synchronization of offline data
 */

import { offlineDB, STORES } from './offlineDB';
import { apiCall } from './api';

class SyncManager {
  constructor() {
    this.isSyncing = false;
    this.listeners = new Set();
    this.syncInterval = null;
    this.isOnline = navigator.onLine;
    
    // Listen for online/offline events
    window.addEventListener('online', () => this.handleOnline());
    window.addEventListener('offline', () => this.handleOffline());
  }

  // Subscribe to sync events
  subscribe(callback) {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  // Notify all listeners
  notify(event) {
    this.listeners.forEach(callback => callback(event));
  }

  handleOnline() {
    this.isOnline = true;
    this.notify({ type: 'online' });
    this.syncAll();
  }

  handleOffline() {
    this.isOnline = false;
    this.notify({ type: 'offline' });
  }

  // Start periodic sync
  startPeriodicSync(intervalMs = 30000) {
    if (this.syncInterval) clearInterval(this.syncInterval);
    this.syncInterval = setInterval(() => {
      if (this.isOnline) this.syncAll();
    }, intervalMs);
  }

  stopPeriodicSync() {
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
      this.syncInterval = null;
    }
  }

  // Get sync status
  async getSyncStatus() {
    const pendingItems = await offlineDB.getPendingSyncItems();
    const failedItems = await offlineDB.getByIndex(STORES.SYNC_QUEUE, 'status', 'failed');
    
    return {
      isOnline: this.isOnline,
      isSyncing: this.isSyncing,
      pendingCount: pendingItems.length,
      failedCount: failedItems.length,
      lastSync: await offlineDB.getUserData('lastSync')
    };
  }

  // Sync all pending items
  async syncAll() {
    if (this.isSyncing || !this.isOnline) return;
    
    this.isSyncing = true;
    this.notify({ type: 'sync_start' });
    
    try {
      const pendingItems = await offlineDB.getPendingSyncItems();
      
      let successCount = 0;
      let failCount = 0;
      
      for (const item of pendingItems) {
        try {
          await this.syncItem(item);
          await offlineDB.removeSyncItem(item.id);
          successCount++;
        } catch (error) {
          await offlineDB.updateSyncItemStatus(item.id, 'failed', error.message);
          failCount++;
        }
      }
      
      await offlineDB.setUserData('lastSync', new Date().toISOString());
      
      this.notify({
        type: 'sync_complete',
        successCount,
        failCount,
        remaining: failCount
      });
      
    } catch (error) {
      this.notify({ type: 'sync_error', error: error.message });
    } finally {
      this.isSyncing = false;
    }
  }

  // Sync a single item
  async syncItem(item) {
    switch (item.action) {
      case 'create':
        await apiCall(item.endpoint, { method: 'POST', body: item.data });
        break;
      case 'update':
        await apiCall(item.endpoint, { method: 'PUT', body: item.data });
        break;
      case 'delete':
        await apiCall(item.endpoint, { method: 'DELETE' });
        break;
      default:
        throw new Error(`Unknown action: ${item.action}`);
    }
    
    // Mark local item as synced
    if (item.data?.id) {
      const storeName = this.getStoreNameForType(item.type);
      if (storeName) {
        const localItem = await offlineDB.get(storeName, item.data.id);
        if (localItem) {
          localItem.synced = true;
          localItem.synced_at = new Date().toISOString();
          await offlineDB.put(storeName, localItem);
        }
      }
    }
  }

  getStoreNameForType(type) {
    const mapping = {
      incident: STORES.INCIDENTS,
      ptp: STORES.PTPS,
      jha: STORES.JHAS,
      near_miss: STORES.NEAR_MISSES,
      ai_scan: STORES.AI_SCANS
    };
    return mapping[type];
  }

  // Save data locally and queue for sync
  async saveOffline(type, data, endpoint, action = 'create') {
    const storeName = this.getStoreNameForType(type);
    
    // Generate local ID if needed
    if (!data.id) {
      data.id = `local_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    }
    
    data.created_at = data.created_at || new Date().toISOString();
    data.synced = false;
    data.offline_created = true;
    
    // Save to local store
    if (storeName) {
      await offlineDB.put(storeName, data);
    }
    
    // Add to sync queue
    await offlineDB.addToSyncQueue(type, action, data, endpoint);
    
    this.notify({ type: 'item_queued', itemType: type });
    
    // Try to sync immediately if online
    if (this.isOnline) {
      setTimeout(() => this.syncAll(), 1000);
    }
    
    return data;
  }

  // Cache API response for offline use
  async cacheAPIResponse(endpoint, data, ttlMinutes = 60) {
    await offlineDB.cacheResponse(endpoint, data, ttlMinutes);
  }

  // Get cached or fetch from API
  async fetchWithCache(endpoint, options = {}) {
    const { ttlMinutes = 60, forceRefresh = false } = options;
    
    // Try cache first if offline or not forcing refresh
    if (!this.isOnline || !forceRefresh) {
      const cached = await offlineDB.getCachedResponse(endpoint);
      if (cached) {
        return { data: cached, fromCache: true };
      }
    }
    
    // Fetch from API if online
    if (this.isOnline) {
      try {
        const data = await apiCall(endpoint);
        
        // Cache the response
        await this.cacheAPIResponse(endpoint, data, ttlMinutes);
        
        return { data, fromCache: false };
      } catch (error) {
        // If fetch fails, try cache
        const cached = await offlineDB.getCachedResponse(endpoint);
        if (cached) {
          return { data: cached, fromCache: true };
        }
        throw error;
      }
    }
    
    throw new Error('Offline and no cached data available');
  }

  // Get all local data (including unsynced)
  async getLocalData(type) {
    const storeName = this.getStoreNameForType(type);
    if (!storeName) return [];
    return offlineDB.getAll(storeName);
  }

  // Clear all offline data
  async clearAllData() {
    await offlineDB.clear(STORES.INCIDENTS);
    await offlineDB.clear(STORES.PTPS);
    await offlineDB.clear(STORES.JHAS);
    await offlineDB.clear(STORES.NEAR_MISSES);
    await offlineDB.clear(STORES.AI_SCANS);
    await offlineDB.clear(STORES.SYNC_QUEUE);
    await offlineDB.clear(STORES.CACHE);
  }

  // Request background sync (if supported)
  async requestBackgroundSync() {
    if ('serviceWorker' in navigator && 'sync' in window.ServiceWorkerRegistration.prototype) {
      try {
        const registration = await navigator.serviceWorker.ready;
        await registration.sync.register('sync-forms');
      } catch (error) {
        // Background sync not available - fail silently
      }
    }
  }

  // AI Scan specific methods
  async saveAIScanOffline(scanData) {
    const id = `offline_scan_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    const offlineScan = {
      id,
      ...scanData,
      status: 'pending', // pending, syncing, synced, failed
      created_at: new Date().toISOString(),
      synced: false,
      offline_created: true
    };
    
    // Save to local store
    await offlineDB.put(STORES.AI_SCANS, offlineScan);
    
    // Add to sync queue
    await offlineDB.addToSyncQueue('ai_scan', 'create', offlineScan, '/api/ai-hazard-scan/analyze');
    
    this.notify({ type: 'ai_scan_queued', scan: offlineScan });
    
    // Try to sync immediately if online
    if (this.isOnline) {
      setTimeout(() => this.syncAll(), 1000);
    }
    
    return offlineScan;
  }

  async getPendingAIScans() {
    // Note: IndexedDB doesn't support boolean values as index keys
    // So we fetch all and filter client-side
    const allScans = await offlineDB.getAll(STORES.AI_SCANS);
    return allScans.filter(scan => scan.synced === false);
  }

  async getAIScanById(id) {
    return offlineDB.get(STORES.AI_SCANS, id);
  }

  async updateAIScanStatus(id, status, result = null) {
    const scan = await offlineDB.get(STORES.AI_SCANS, id);
    if (scan) {
      scan.status = status;
      if (result) {
        scan.result = result;
        scan.synced = true;
        scan.synced_at = new Date().toISOString();
      }
      await offlineDB.put(STORES.AI_SCANS, scan);
      this.notify({ type: 'ai_scan_updated', scan });
    }
    return scan;
  }

  async removeAIScan(id) {
    await offlineDB.delete(STORES.AI_SCANS, id);
    this.notify({ type: 'ai_scan_removed', id });
  }

  async getAllLocalAIScans() {
    return offlineDB.getAll(STORES.AI_SCANS);
  }
}

// Singleton instance
const syncManager = new SyncManager();

export { syncManager };
export default syncManager;
